import {
  put, call,
} from 'lib/redux';
import * as types from 'constants/ActionTypes';
import { decodeAndDecrypt } from 'utils/app.utils';
import { KEY, INITIALIZATION_VECTOR } from 'constants/AppConstants';
import { fetchOrderClosureReasonsSaga } from 'features/cancel-order-dialog/CancelOrder.saga';
import getReportDetailsService from 'services/getReportDetails.service';
import geocodeService from 'services/geocode.service';
import { isAuthorized } from 'utils/auth.utils';
import history from 'store/CreateHistory';
import { logError } from 'utils/logger.utils';
import {
  extractZipCodeFromText,
  isStoredAddressUnresolvable,
  hasUsableGeocodeCoordinates,
  getGeocodedAddressFromAddressResponse,
} from 'features/order/Order.utils';

/**
 * Try zip geocode queries in order (array iteration — no for-of / regenerator loops).
 */
function* tryZipFallbackQuery(queries, index, country, zip) {
  if (index >= queries.length) return null;

  const query = queries[index];
  try {
    const zipResponse = yield call(geocodeService, query, country);
    if (zipResponse.ok && hasUsableGeocodeCoordinates(zipResponse.json?.lat, zipResponse.json?.lon)) {
      const zipGeocoded = getGeocodedAddressFromAddressResponse(zipResponse.json);
      return {
        latitude: zipGeocoded.latitude,
        longitude: zipGeocoded.longitude,
        zip,
      };
    }
  } catch (error) {
    logError({
      message: 'Zip fallback geocode failed in address verification',
      error: error.message,
      query,
      zip,
    });
  }

  return yield call(tryZipFallbackQuery, queries, index + 1, country, zip);
}

/**
 * Resolve usable map coordinates from zip (and city/state+zip as secondary query)
 * when report lat/lon are missing or 0,0.
 */
function* resolveZipFallbackCoordinates(reportAddress) {
  const country = reportAddress?.country || 'US';
  const zip = reportAddress?.zip
    || extractZipCodeFromText(reportAddress?.address || '', country);

  if (!zip) return null;

  const queries = [zip];
  if (reportAddress?.city && reportAddress?.state) {
    queries.push(`${reportAddress.city}, ${reportAddress.state} ${zip}`);
  }

  return yield call(tryZipFallbackQuery, queries, 0, country, zip);
}

export default function* loadAddressVerificationSaga({ reportId }) {
  try {
    yield put({ type: types.LOADING_ADDRESS_VERIFICATION_PAGE });
    if (isAuthorized()) yield call(fetchOrderClosureReasonsSaga);

    const signature = new URLSearchParams(history.location.search).get('signature'); // #genai
    const decryptedReportId = yield call(decodeAndDecrypt, reportId, KEY, INITIALIZATION_VECTOR);
    const { ok, json: reportDetailsResponse } = yield call(getReportDetailsService, Number(decryptedReportId), signature);
    if (!ok) {
      const error = new Error(reportDetailsResponse.errorDescription);
      error.errorCode = reportDetailsResponse.errorCode;
      throw error;
    }
    const { building, reportAddress, ...rest } = reportDetailsResponse;

    let mapAddress = reportAddress;
    let usedZipFallback = false;

    // Zero / missing coords (common on NeedToID reports) → center on zip.
    if (isStoredAddressUnresolvable(reportAddress)) {
      const fallbackCoords = yield call(resolveZipFallbackCoordinates, reportAddress);
      if (fallbackCoords) {
        mapAddress = {
          ...reportAddress,
          ...fallbackCoords,
        };
        usedZipFallback = true;
      }
    }

    yield put({
      type: types.LOAD_ADDRESS_VERIFICATION_PAGE_SUCCESS,
      addressVerificationMapPoint: {
        building,
        address: mapAddress,
        usedZipFallback,
        reportId: Number(decryptedReportId),
      },
      reportDetails: rest,
      reportId: Number(decryptedReportId),
    });
  } catch (err) {
    yield put({ type: types.LOAD_ADDRESS_VERIFICATION_PAGE_ERROR });
  }
}
